import { useTheme } from "@/app/providers/theme-provider"
import { useBothLogosValidation } from "@/hooks/use-logo-validation"
import { getInjectedAsset, injectedAspectRatio } from "@/lib/runtime-branding"
import { cn } from "@/lib/utils"

import { resolveInjectedLandingLogo } from "./hello-ai-logo-utils"

// Optional customer landing logos — served from public/ so they work in production
// without Vite processing. Customers can place these in public/customer/ to override
// the embedded typographic wordmark.
const logoLightUrl = `${import.meta.env.BASE_URL}customer/landing-logo-light.svg`
const logoDarkUrl = `${import.meta.env.BASE_URL}customer/landing-logo-dark.svg`

type HelloAILogoProps = {
  className?: string
  /** Brand "focus" motion — the mark enters blurred and sharpens into place. */
  entranceAnimation?: boolean
  /** Same knob as `<Greeting typewriterStartDelayMs />` so the two stay aligned on ChatHome. */
  typewriterStartDelayMs?: number
  /** @deprecated Per-path stagger is gone; accepted so existing call sites keep compiling. */
  staggerDurationMs?: number
  /** @deprecated Per-path stagger is gone; accepted so existing call sites keep compiling. */
  staggerShape?: "linear" | "ease-out-quad" | "ease-in-quad"
}

/**
 * The landing lockup. The product name stays "Hello AI" and is set as a
 * typographic headline (ABC Solar Ultra via `type-headline`) — never as a
 * wordmark SVG. Customer assets still win: injected asset → public/customer
 * landing logo → this headline.
 */
export function HelloAILogo({
  className,
  entranceAnimation = false,
  typewriterStartDelayMs = 0,
}: HelloAILogoProps) {
  const { theme } = useTheme()
  const logoStatus = useBothLogosValidation(logoLightUrl, logoDarkUrl)

  let effectiveTheme: "light" | "dark"
  if (theme === "system") {
    effectiveTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  } else {
    effectiveTheme = theme
  }

  const entranceDelayStyle =
    entranceAnimation && typewriterStartDelayMs > 0
      ? { animationDelay: `${typewriterStartDelayMs}ms` }
      : undefined

  const injected = resolveInjectedLandingLogo(
    getInjectedAsset("logo_landing_light"),
    getInjectedAsset("logo_landing_dark"),
    effectiveTheme
  )
  if (injected) {
    return (
      <img
        alt="Logo"
        src={injected.url}
        className={cn(
          "h-auto transition-opacity duration-200",
          entranceAnimation && "brand-focus-entrance",
          className
        )}
        style={{
          objectFit: "contain",
          aspectRatio: injectedAspectRatio(injected),
          ...entranceDelayStyle,
        }}
      />
    )
  }

  if (logoStatus === "pending") {
    return <div aria-hidden className={cn(className)} style={{ aspectRatio: "302.02 / 43.33" }} />
  }

  if (logoStatus === "invalid") {
    return (
      <h1
        data-testid="landing-wordmark"
        style={entranceDelayStyle}
        className={cn(
          "logo type-headline text-foreground text-center text-[clamp(2.75rem,7vw,5.5rem)]",
          entranceAnimation && "brand-focus-entrance",
          className
        )}
      >
        Hello AI
      </h1>
    )
  }

  const logoSrc = effectiveTheme === "dark" ? logoDarkUrl : logoLightUrl

  return (
    <img
      alt="Logo"
      src={logoSrc}
      style={{ objectFit: "contain", ...entranceDelayStyle }}
      className={cn(
        "h-auto transition-opacity duration-200",
        entranceAnimation && "brand-focus-entrance",
        className
      )}
    />
  )
}
